import React from "react";
import {
    Flex,
    FormControl,
    Icon,
    Text,
    useDisclosure,
    Tooltip,
    Button,
} from "@chakra-ui/react";

import { FaFilePdf, FaFileWord, FaFile, FaDownload } from "react-icons/fa6";
import utils from "../utils/commonFunctions";
import PdfViewerModal from "./PdfViewer/PdfViewerModal";

const getExtensionFromPath = (filePath) => {
    if (!filePath) return '';
    const clean = filePath.split('?')[0]; // strip query string (e.g. SAS tokens)
    const parts = clean.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

const getFileIcon = (ext) => {
    switch (ext) {
        case 'pdf': return FaFilePdf;
        case 'doc':
        case 'docx': return FaFileWord;
        default: return FaFile;
    }
};

const getIconColor = (ext) => {
    switch (ext) {
        case 'pdf': return 'red.400';
        case 'doc':
        case 'docx': return 'blue.400';
        default: return 'gray.400';
    }
};

const FileDisplay = ({ label, filePath }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const ext = getExtensionFromPath(filePath);
    const isPdf = ext === 'pdf';

    if (!filePath) return null;

    // PDF → open inline viewer modal
    if (isPdf) {
        return (
            <>
                <FormControl id={label}>
                    <Tooltip label="Click to View File">
                        <Flex
                            as="button"
                            type="button"
                            onClick={onOpen}
                            alignItems="center"
                            h="80px"
                            borderRadius="lg"
                            bg="white"
                            w="full"
                            boxShadow="md"
                            _hover={{ bg: 'gray.50' }}
                        >
                            <Flex justify="center" align="center" h="full" borderLeftRadius="lg" px={4}>
                                <Icon as={FaFilePdf} color="red.400" fontSize={{ base: '32px', md: '48px' }} />
                            </Flex>
                            <Text fontWeight="bold" px={2} isTruncated color="gray.400">
                                {utils.getOriginalFileName(filePath)}
                            </Text>
                        </Flex>
                    </Tooltip>
                </FormControl>
                <PdfViewerModal filePath={filePath} isOpen={isOpen} onOpen={onOpen} onClose={onClose} />
            </>
        );
    }

    // DOCX / other → download button (cannot be rendered inline in the browser)
    return (
        <FormControl id={label}>
            <Flex
                alignItems="center"
                h="80px"
                borderRadius="lg"
                bg="white"
                w="full"
                boxShadow="md"
            >
                <Flex justify="center" align="center" h="full" borderLeftRadius="lg" px={4}>
                    <Icon as={getFileIcon(ext)} color={getIconColor(ext)} fontSize={{ base: '32px', md: '48px' }} />
                </Flex>
                <Text fontWeight="bold" px={2} isTruncated color="gray.400" flex={1}>
                    {utils.getOriginalFileName(filePath)}
                </Text>
                <Tooltip label="Download File">
                    <Button
                        as="a"
                        href={filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        mr={4}
                        leftIcon={<Icon as={FaDownload} />}
                    >
                        Download
                    </Button>
                </Tooltip>
            </Flex>
        </FormControl>
    );
};

export default FileDisplay;
